import React from 'react'
import procvImage from '../../assets/procv.png'
import miniCo2Image from '../../assets/mini-co2.png'
import ctdImage from '../../assets/ctd.png'
import fluorometerImage from '../../assets/fluorometer.png'
import phImage from '../../assets/ph.png'

const AboutPage = () => {
  return (
    <div style={{ 
      padding: '2rem', 
      width: '100%',
      color: '#495057',
      boxSizing: 'border-box'
    }}>
      <h1 style={{ 
        color: '#2c3e50', 
        textAlign: 'center', 
        marginBottom: '2rem',
        borderBottom: '3px solid #007bff',
        paddingBottom: '1rem'
      }}>
        About the Ocean Alk-Align Buoy Dashboard
      </h1>

      {/* Sensor Descriptions */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ color: '#2c3e50', marginBottom: '1.5rem' }}>Sensor Descriptions</h2>
        
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {/* ProCV Sensor */}
          <div style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'flex',
            gap: '2rem'
          }}>
            <div style={{ flex: '1' }}>
              <h3 style={{ color: '#007bff', marginBottom: '0.5rem', textAlign: 'center' }}>
                <a 
                  href="/manuals/CO2-Pro CV_Manual 4.3.1.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#007bff', 
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  ProCV Sensor
                  <span style={{ fontSize: '0.8rem', color: '#6c757d' }}></span>
                </a>
              </h3>
              <p style={{ textAlign: 'center', color: '#6c757d', fontSize: '0.9rem', marginBottom: '1rem', fontStyle: 'italic' }}>
                ProOceanus
              </p>
              <p style={{ lineHeight: '1.6', marginBottom: '1rem' }}>
                The ProCV sensor measures CO₂ concentration (xCO₂) in air and water by ventilating samples through an infrared gas analyzer (IRGA). 
                It outputs raw and zero-reference A/D counts, CO₂ concentration (ppmv), humidity, gas pressure, and temperatures.
              </p>
              <h4 style={{ color: '#495057', marginBottom: '0.5rem' }}>Key Functions:</h4>
              <ul style={{ lineHeight: '1.6', marginLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>Measures gaseous CO₂ concentrations in both air and water modes</li>
                <li>Outputs reference and current A/D counts for calibration verification</li>
                <li>Monitors IRGA temperature, humidity, gas pressure, and voltage</li>
              </ul>
              
              <div style={{ 
                background: '#f8f9fa', 
                padding: '1rem', 
                borderRadius: '6px',
                border: '1px solid #e9ecef',
                marginTop: '1rem'
              }}>
                <h4 style={{ color: '#007bff', marginBottom: '0.5rem' }}>Derived Value: pCO₂ (Partial Pressure of CO₂)</h4>
                <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                  <strong>Formula:</strong> pCO₂ = xCO₂ (ppmv) × P (atm)
                </p>
                <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                  <strong>Where:</strong> xCO₂ is the corrected CO₂ concentration from the ProCV sensor, and P is gas pressure in atm (converted from mbar as P = mbar / 1013.25).
                </p>
                <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                  <strong>Units:</strong> μatm (microatmospheres)
                </p>
                <p style={{ lineHeight: '1.6' }}>
                  <strong>Process:</strong> Temperature and humidity corrections are applied as per device calibration constants before the pCO₂ derivation. Essential for calculating air-sea CO₂ flux and understanding carbon cycling in marine environments.
                </p>
              </div>
            </div>
            
            {/* Image on the right */}
            <div style={{ 
              flex: '0 0 170px', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '600px'
            }}>
              <img 
                src={procvImage} 
                alt="ProCV Sensor" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain',
                  borderRadius: '8px'
                }} 
              />
            </div>
          </div>

          {/* Mini CO₂ Sensor */}
          <div style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'flex',
            gap: '2rem'
          }}>
            <div style={{ flex: '1' }}>
              <h3 style={{ color: '#007bff', marginBottom: '0.5rem', textAlign: 'center' }}>
                <a 
                  href="/manuals/Mini_CO2_Digital_Manual_3.4.0.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#007bff', 
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  Mini CO₂ Sensor
                  <span style={{ fontSize: '0.8rem', color: '#6c757d' }}></span>
                </a>
              </h3>
              <p style={{ textAlign: 'center', color: '#6c757d', fontSize: '0.9rem', marginBottom: '1rem', fontStyle: 'italic' }}>
                ProOceanus
              </p>
              <p style={{ lineHeight: '1.6', marginBottom: '1rem' }}>
                A compact submersible CO₂ sensor designed for long-term autonomous deployment. 
                It uses a non-dispersive infrared (NDIR) detector to measure dissolved CO₂ concentrations, outputting corrected CO₂ (ppmv),
                pressure, temperature, and A/D counts.
              </p>
              <h4 style={{ color: '#495057', marginBottom: '0.5rem' }}>Key Functions:</h4>
              <ul style={{ lineHeight: '1.6', marginLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>Measures dissolved CO₂ in seawater with in-situ pressure and temperature compensation</li>
                <li>Outputs corrected CO₂ (ppmv), gas pressure, IR detector temperature, and A/D counts</li>
                <li>Supports both air and water mode sampling</li>
                <li>Enables autonomous monitoring with minimal power consumption</li>
              </ul>
              
              <div style={{ 
                background: '#f8f9fa', 
                padding: '1rem', 
                borderRadius: '6px',
                border: '1px solid #e9ecef',
                marginTop: '1rem'
              }}>
                <h4 style={{ color: '#007bff', marginBottom: '0.5rem' }}>Derived Value: pCO₂ (Partial Pressure of CO₂)</h4>
                <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                  <strong>Formula:</strong> pCO₂ = xCO₂ (ppmv) × P (atm)
                </p>
                <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                  <strong>Where:</strong> xCO₂ is the corrected CO₂ concentration from the Mini CO₂ sensor, and P is gas pressure in atm (converted from mbar as P = mbar / 1013.25).
                </p>
                <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                  <strong>Units:</strong> μatm (microatmospheres)
                </p>
                <p style={{ lineHeight: '1.6' }}>
                  <strong>Process:</strong> pCO₂ is computed by multiplying corrected CO₂ by the ambient gas pressure (atm). Temperature and pressure corrections are applied for accurate dissolved CO₂ measurements in marine environments.
                </p>
              </div>
            </div>
            
            {/* Image on the right */}
            <div style={{ 
              flex: '0 0 170px', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '600px'
            }}>
              <img 
                src={miniCo2Image} 
                alt="Mini CO₂ Sensor" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain',
                  borderRadius: '8px'
                }} 
              />
            </div>
          </div>

          {/* CTD Sensor */}
          <div style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'flex',
            gap: '2rem'
          }}>
            <div style={{ flex: '1' }}>
              <h3 style={{ color: '#007bff', marginBottom: '0.5rem', textAlign: 'center' }}>
                <a 
                  href="/manuals/37SMP-ODO_RS232_004.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#007bff', 
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  CTD (Conductivity, Temperature, Depth) Sensor
                  <span style={{ fontSize: '0.8rem', color: '#6c757d' }}></span>
                </a>
              </h3>
              <p style={{ textAlign: 'center', color: '#6c757d', fontSize: '0.9rem', marginBottom: '1rem', fontStyle: 'italic' }}>
                Sea-Bird Scientific
              </p>
              <p style={{ lineHeight: '1.6', marginBottom: '1rem' }}>
                The CTD sensor records seawater temperature (ITS-90), conductivity (S/m), and pressure (dbar).
                The sensor also measures dissolved oxygen (ml/L), calculates sound velocity (m/s), and specific conductivity.
              </p>
              <h4 style={{ color: '#495057', marginBottom: '0.5rem' }}>Key Functions:</h4>
              <ul style={{ lineHeight: '1.6', marginLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>High-accuracy measurements of seawater temperature, conductivity, and depth</li>
                <li>Calculates salinity and sound velocity in real-time</li>
                <li>Outputs oxygen concentrations and specific conductivity</li>
                <li>Provides reference pressure for buoy positioning and depth-based corrections</li>
              </ul>
              
              <div style={{ 
                background: '#f8f9fa', 
                padding: '1rem', 
                borderRadius: '6px',
                border: '1px solid #e9ecef',
                marginTop: '1rem'
              }}>
                <h4 style={{ color: '#007bff', marginBottom: '0.5rem' }}>Derived Values:</h4>
                
                <div style={{ marginBottom: '1rem' }}>
                  <h5 style={{ color: '#495057', marginBottom: '0.5rem' }}>Salinity (PSU)</h5>
                  <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                    <strong>Process:</strong> Derived from conductivity, temperature, and pressure using the TEOS-10 standard. Conductivity (S/m) is converted to salinity through equations accounting for temperature and depth (pressure) compensation.
                  </p>
                  <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                    <strong>Units:</strong> PSU (Practical Salinity Units)
                  </p>
                  <p style={{ lineHeight: '1.6' }}>
                    <strong>Significance:</strong> Fundamental oceanographic parameter that affects water density, CO₂ solubility, and biological processes.
                  </p>
                </div>
                
                <div>
                  <h5 style={{ color: '#495057', marginBottom: '0.5rem' }}>Sound Velocity (m/s)</h5>
                  <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                    <strong>Formula:</strong> Calculated from salinity, temperature, and pressure using the Chen & Millero (1977) equation: c = f(S, T, P)
                  </p>
                  <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                    <strong>Units:</strong> m/s (meters per second)
                  </p>
                  <p style={{ lineHeight: '1.6' }}>
                    <strong>Significance:</strong> Critical for acoustic propagation modeling and is output directly by the CTD firmware.
                  </p>
                </div>
              </div>
            </div>
            <div style={{ 
              flex: '0 0 270px', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px'
            }}>
              <img 
                src={ctdImage} 
                alt="CTD Sensor" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain',
                  borderRadius: '8px'
                }} 
              />
            </div>
          </div>

          {/* Wetlabs BBFL2BAC Fluorometer */}
          <div style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'flex',
            gap: '2rem'
          }}>
            <div style={{ flex: '1' }}>
              <h3 style={{ color: '#007bff', marginBottom: '0.5rem', textAlign: 'center' }}>
                <a 
                  href="/manuals/ECOMasterc.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#007bff', 
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  BBFL2BAC Fluorometer
                  <span style={{ fontSize: '0.8rem', color: '#6c757d' }}></span>
                </a>
              </h3>
              <p style={{ textAlign: 'center', color: '#6c757d', fontSize: '0.9rem', marginBottom: '1rem', fontStyle: 'italic' }}>
                Wetlabs
              </p>
              <p style={{ lineHeight: '1.6', marginBottom: '1rem' }}>
                A three-channel fluorometer measuring chlorophyll-a (Chl), colored dissolved organic matter (CDOM), 
                and backscatter (650 nm). Raw counts are scaled using calibration factors and dark counts from characterization sheets.
              </p>
              <h4 style={{ color: '#495057', marginBottom: '0.5rem' }}>Key Functions:</h4>
              <ul style={{ lineHeight: '1.6', marginLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>Measures chlorophyll fluorescence for estimating phytoplankton biomass</li>
                <li>Detects CDOM concentrations indicative of organic matter dynamics</li>
                <li>Provides optical backscatter at 650 nm for turbidity assessments</li>
                <li>Real-time scaling of raw sensor outputs to engineering units</li>
              </ul>
              
              <div style={{ 
                background: '#f8f9fa', 
                padding: '1rem', 
                borderRadius: '6px',
                border: '1px solid #e9ecef',
                marginTop: '1rem'
              }}>
                <h4 style={{ color: '#007bff', marginBottom: '0.5rem' }}>Derived Values:</h4>
                
                <div style={{ marginBottom: '1rem' }}>
                  <h5 style={{ color: '#495057', marginBottom: '0.5rem' }}>Chlorophyll (µg/L)</h5>
                  <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                    <strong>Formula:</strong> Chl (µg/L) = Scale Factor × (Chl Counts - Dark Counts)
                  </p>
                  <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                    <strong>Process:</strong> Raw fluorescence counts are scaled using sensor-specific calibration factors and dark counts from characterization sheets.
                  </p>
                  <p style={{ lineHeight: '1.6' }}>
                    <strong>Significance:</strong> Estimates phytoplankton biomass and primary productivity in marine environments.
                  </p>
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <h5 style={{ color: '#495057', marginBottom: '0.5rem' }}>CDOM (ppb)</h5>
                  <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                    <strong>Formula:</strong> CDOM (ppb) = Scale Factor × (CDOM Counts - Dark Counts)
                  </p>
                  <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                    <strong>Process:</strong> Colored dissolved organic matter fluorescence is scaled using calibration factors and dark counts.
                  </p>
                  <p style={{ lineHeight: '1.6' }}>
                    <strong>Significance:</strong> Indicates organic matter dynamics and water quality parameters.
                  </p>
                </div>
                
                <div>
                  <h5 style={{ color: '#495057', marginBottom: '0.5rem' }}>Backscatter (m⁻¹ sr⁻¹)</h5>
                  <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                    <strong>Formula:</strong> Backscatter (m⁻¹ sr⁻¹) = Scale Factor × (Backscatter Counts - Dark Counts)
                  </p>
                  <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                    <strong>Process:</strong> Optical backscatter at 650 nm is computed using its respective scale factor and dark counts.
                  </p>
                  <p style={{ lineHeight: '1.6' }}>
                    <strong>Significance:</strong> Provides turbidity assessments and particle concentration estimates.
                  </p>
                </div>
              </div>
            </div>
            <div style={{ 
              flex: '0 0 300px', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px'
            }}>
              <img 
                src={fluorometerImage} 
                alt="Fluorometer" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain',
                  borderRadius: '8px'
                }} 
              />
            </div>
          </div>

          {/* PyroScience AquaPHOX-L-pH Sensor */}
          <div style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'flex',
            gap: '2rem'
          }}>
            <div style={{ flex: '1' }}>
              <h3 style={{ color: '#007bff', marginBottom: '0.5rem', textAlign: 'center' }}>
                <a 
                  href="/manuals/Manual_AquapHOx-L.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#007bff', 
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  AquaPHOX-L-pH Sensor
                  <span style={{ fontSize: '0.8rem', color: '#6c757d' }}></span>
                </a>
              </h3>
              <p style={{ textAlign: 'center', color: '#6c757d', fontSize: '0.9rem', marginBottom: '1rem', fontStyle: 'italic' }}>
                PyroScience
              </p>
              <p style={{ lineHeight: '1.6', marginBottom: '1rem' }}>
                The AquaPHOX sensor uses optical (ISFET-based) technology for long-term monitoring of seawater pH (total scale).
                Raw voltage outputs are processed with calibration coefficients and corrected for temperature, salinity, and pressure.
              </p>
              <h4 style={{ color: '#495057', marginBottom: '0.5rem' }}>Key Functions:</h4>
              <ul style={{ lineHeight: '1.6', marginLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>High-precision pH measurements using ISFET technology</li>
                <li>Temperature and salinity compensated pH (total scale)</li>
                <li>Long-term deployments with low drift and optical stability</li>
                <li>Supports ocean acidification studies and carbonate chemistry assessments</li>
              </ul>
              
              <div style={{ 
                background: '#f8f9fa', 
                padding: '1rem', 
                borderRadius: '6px',
                border: '1px solid #e9ecef',
                marginTop: '1rem'
              }}>
                <h4 style={{ color: '#007bff', marginBottom: '0.5rem' }}>Derived Value: pH (Total Scale)</h4>
                <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                  <strong>Formula:</strong> pH = (VREF - K₀ - K₂·T) / S_Nernst + corrections for Cl⁻ and SO₄²⁻ activity
                </p>
                <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                  <strong>Process:</strong> pH is computed from raw ISFET voltage outputs using calibration coefficients (K₀, K₂) and environmental parameters. Where S_Nernst is temperature dependent, and additional corrections are applied for ionic strength and pressure effects.
                </p>
                <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
                  <strong>Units:</strong> pH (total scale)
                </p>
                <p style={{ lineHeight: '1.6' }}>
                  <strong>Significance:</strong> High-precision pH measurements with temperature and salinity compensation for ocean acidification studies and carbonate chemistry assessments.
                </p>
              </div>
            </div>
            
            {/* Image on the right */}
            <div style={{ 
              flex: '0 0 170px', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '600px'
            }}>
              <img 
                src={phImage} 
                alt="pH Sensor" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain',
                  borderRadius: '8px'
                }} 
              />
            </div>
          </div>
        </div>
      </section>



      {/* Parameter Correlations */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ color: '#2c3e50', marginBottom: '1.5rem' }}>Expected Parameter Correlations</h2>
        
        <div style={{ 
          background: '#e7f3ff', 
          padding: '2rem', 
          borderRadius: '8px',
          border: '1px solid #b3d9ff'
        }}>
          <p style={{ lineHeight: '1.6', marginBottom: '1.5rem' }}>
            Understanding parameter correlations helps identify data quality issues and provides insights 
            into environmental processes. Here are key correlations to expect in marine environments:
          </p>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '1.5rem' 
          }}>
            {/* Temperature and CO₂ Solubility */}
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Temperature and CO₂ Solubility</h4>
              <ul style={{ lineHeight: '1.6', marginLeft: '1.5rem' }}>
                <li><strong>Negative correlation:</strong> As temperature increases, CO₂ solubility decreases</li>
                <li><strong>Expected pattern:</strong> Higher pCO₂ values during warm periods</li>
                <li><strong>Seasonal variation:</strong> Summer typically shows higher pCO₂ than winter</li>
              </ul>
            </div>

            {/* Salinity and CO₂ Solubility */}
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Salinity and CO₂ Solubility</h4>
              <ul style={{ lineHeight: '1.6', marginLeft: '1.5rem' }}>
                <li><strong>Positive correlation:</strong> Higher salinity generally increases CO₂ solubility</li>
                <li><strong>Expected pattern:</strong> Freshwater inputs may reduce CO₂ solubility</li>
                <li><strong>Regional variation:</strong> Estuarine areas show strong salinity-CO₂ relationships</li>
              </ul>
            </div>

            {/* Temperature and Conductivity */}
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Temperature and Conductivity</h4>
              <ul style={{ lineHeight: '1.6', marginLeft: '1.5rem' }}>
                <li><strong>Positive correlation:</strong> Conductivity increases with temperature</li>
                <li><strong>Expected pattern:</strong> Seasonal temperature cycles affect conductivity</li>
                <li><strong>Quality check:</strong> Sudden conductivity changes without temperature changes may indicate sensor issues</li>
              </ul>
            </div>

            {/* Oxygen and Temperature */}
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Oxygen and Temperature</h4>
              <ul style={{ lineHeight: '1.6', marginLeft: '1.5rem' }}>
                <li><strong>Negative correlation:</strong> Oxygen solubility decreases with increasing temperature</li>
                <li><strong>Expected pattern:</strong> Lower oxygen levels during warm periods</li>
                <li><strong>Biological influence:</strong> Photosynthesis and respiration can override temperature effects</li>
              </ul>
            </div>

            {/* Pressure and Depth */}
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Pressure and Depth</h4>
              <ul style={{ lineHeight: '1.6', marginLeft: '1.5rem' }}>
                <li><strong>Linear relationship:</strong> Pressure increases approximately 1 dbar per meter of depth</li>
                <li><strong>Expected pattern:</strong> Consistent pressure-depth relationship</li>
                <li><strong>Quality check:</strong> Sudden pressure changes may indicate sensor movement or biofouling</li>
              </ul>
            </div>
          </div>

          <div style={{ 
            background: 'white', 
            padding: '1rem', 
            borderRadius: '6px',
            marginTop: '1.5rem',
            border: '1px solid #dee2e6'
          }}>
            <h4 style={{ color: '#dc3545', marginBottom: '0.5rem' }}>Data Quality Indicators</h4>
            <p style={{ lineHeight: '1.6', marginBottom: '0.5rem' }}>
              <strong>Watch for:</strong> Unusual correlations or sudden changes that don't follow expected patterns. 
              These may indicate sensor malfunction, biofouling, or environmental events requiring investigation.
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}

export default AboutPage 